import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    _base_domain_: [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'users._base_domain_': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/restaurants-availability-exceptions',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_restaurants_availability-exceptions';

/** Creates a new availability exception. */
export function createAvailabilityException(
  payload: object
): RequestOptionsFactory<any> {
  function __createAvailabilityException({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'availabilityException.createdDate' },
          { path: 'availabilityException.updatedDate' },
          { path: 'availabilityException.startTime' },
          { path: 'availabilityException.endTime' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.CreateAvailabilityException',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/availability-exceptions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'availabilityException.createdDate' },
              { path: 'availabilityException.updatedDate' },
              { path: 'availabilityException.startTime' },
              { path: 'availabilityException.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createAvailabilityException;
}

/** Retrieves an availability exception. */
export function getAvailabilityException(
  payload: object
): RequestOptionsFactory<any> {
  function __getAvailabilityException({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'GET' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.GetAvailabilityException',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/availability-exceptions/{availabilityExceptionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'availabilityException.createdDate' },
              { path: 'availabilityException.updatedDate' },
              { path: 'availabilityException.startTime' },
              { path: 'availabilityException.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAvailabilityException;
}

/**
 * Updates an availability exception.
 *
 * Each time the availability exception is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the availability exception.
 * This ensures you're working with the latest availability exception and prevents unintended overwrites.
 */
export function updateAvailabilityException(
  payload: object
): RequestOptionsFactory<any> {
  function __updateAvailabilityException({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'availabilityException.createdDate' },
          { path: 'availabilityException.updatedDate' },
          { path: 'availabilityException.startTime' },
          { path: 'availabilityException.endTime' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'PATCH' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.UpdateAvailabilityException',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/availability-exceptions/{availabilityException.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'availabilityException.createdDate' },
              { path: 'availabilityException.updatedDate' },
              { path: 'availabilityException.startTime' },
              { path: 'availabilityException.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateAvailabilityException;
}

/** Deletes an availability exception. */
export function deleteAvailabilityException(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteAvailabilityException({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'DELETE' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.DeleteAvailabilityException',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/availability-exceptions/{availabilityExceptionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAvailabilityException;
}

/**
 * Creates a query to retrieve a list of availability exceptions.
 *
 * The `queryAvailabilityExceptions()` function builds a query to retrieve a list of availability exceptions and returns an `AvailabilityExceptionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/availability-exceptions/availability-exceptions-query-builder/find) function.
 *
 * You can refine the query by chaining `AvailabilityExceptionsQueryBuilder` functions onto the query. `AvailabilityExceptionsQueryBuilder` functions enable you to filter, sort, and control the results that `queryAvailabilityExceptions()` returns.
 *
 * `queryAvailabilityExceptions()` runs with the following `AvailabilityExceptionsQueryBuilder` defaults, which you can override:
 *
 * - [`limit(50)`](/availability-exceptions/availability-exceptions-query-builder/limit)
 * - [`ascending('_id')`](/availability-exceptions/availability-exceptions-query-builder/ascending)
 *
 * The following `AvailabilityExceptionsQueryBuilder` functions are supported for `queryAvailabilityExceptions()`. For a full description of the availability exception object, see the object returned for the [`items`](/availability-exceptions/availability-exceptions-query-result/items) property in `AvailabilityExceptionsQueryResult`.
 */
export function queryAvailabilityExceptions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryAvailabilityExceptions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.QueryAvailabilityExceptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/availability-exceptions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'availabilityExceptions.createdDate' },
              { path: 'availabilityExceptions.updatedDate' },
              { path: 'availabilityExceptions.startTime' },
              { path: 'availabilityExceptions.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryAvailabilityExceptions;
}

/** Creates multiple availability exceptions. */
export function bulkCreateAvailabilityExceptions(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateAvailabilityExceptions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'availabilityExceptions.createdDate' },
          { path: 'availabilityExceptions.updatedDate' },
          { path: 'availabilityExceptions.startTime' },
          { path: 'availabilityExceptions.endTime' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.BulkCreateAvailabilityExceptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/bulk/availability-exceptions/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.availabilityException.createdDate' },
              { path: 'results.availabilityException.updatedDate' },
              { path: 'results.availabilityException.startTime' },
              { path: 'results.availabilityException.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateAvailabilityExceptions;
}

/** Updates multiple availability exceptions. */
export function bulkUpdateAvailabilityExceptions(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateAvailabilityExceptions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'availabilityExceptions.mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'availabilityExceptions.availabilityException.createdDate' },
          { path: 'availabilityExceptions.availabilityException.updatedDate' },
          { path: 'availabilityExceptions.availabilityException.startTime' },
          { path: 'availabilityExceptions.availabilityException.endTime' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.BulkUpdateAvailabilityExceptions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/bulk/availability-exceptions/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
              { path: 'results.item.startTime' },
              { path: 'results.item.endTime' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateAvailabilityExceptions;
}

/**
 * Synchronously update tags on multiple availability exceptions.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateAvailabilityExceptionTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateAvailabilityExceptionTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.BulkUpdateAvailabilityExceptionTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/bulk/availability-exceptions/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateAvailabilityExceptionTags;
}

/**
 * Asynchronously update tags on multiple availability exceptions according to the specified filter.
 * If a filter isn't specified, this method updates all availability exceptions.
 * If you specify a tag in both `assignTags` and `unassignTags`, it is assigned.
 */
export function bulkUpdateAvailabilityExceptionTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateAvailabilityExceptionTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.restaurants.v1.availability_exception',
      method: 'POST' as any,
      methodFqn:
        'wix.restaurants.v1.AvailabilityExceptionsService.BulkUpdateAvailabilityExceptionTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixRestaurantsV1AvailabilityExceptionsServiceUrl({
        protoPath: '/v1/bulk/availability-exceptions/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateAvailabilityExceptionTagsByFilter;
}
